import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../i18n';
import { api } from '../../lib/api';
import {
  PLATFORM_LINKEDIN,
  PLATFORM_INSTAGRAM,
  PLATFORM_FACEBOOK,
} from '../../lib/constants';

const PLATFORMS = [PLATFORM_LINKEDIN, PLATFORM_INSTAGRAM, PLATFORM_FACEBOOK];

// Modal opened from Event Detail (and reusable elsewhere). On success it
// POSTs a new draft and navigates to /drafts/:newDraftId.
//
// Props:
//   eventId:  string | null          (the event to attach this draft to, or null for standalone)
//   onClose:  () => void
//   onCreated?: (draft) => void      optional hook before navigation
export function CreateDraftModal({ eventId, onClose, onCreated }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [platform, setPlatform] = useState(PLATFORM_LINKEDIN);
  const [captionPl, setCaptionPl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const created = await api.drafts.create({
        eventId: eventId ?? null,
        platform,
        captionPl: captionPl.trim() || ' ', // backend requires non-undefined string
        contentPhase: eventId ? 'pre_event' : 'standalone',
      });
      onCreated?.(created);
      navigate(`/drafts/${created.id}`);
    } catch (err) {
      setError(err?.message || t('draft.create.error'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={() => !submitting && onClose()}
    >
      <form
        className="modal modal--form"
        onClick={(e) => e.stopPropagation()}
        onSubmit={handleSubmit}
        noValidate
      >
        <h2 className="modal__title">{t('draft.create.title')}</h2>

        <div className="form-field">
          <span className="form-field__label">{t('draft.create.platformLabel')}</span>
          <div className="radio-group">
            {PLATFORMS.map((p) => (
              <label key={p}>
                <input
                  type="radio"
                  name="create-draft-platform"
                  checked={platform === p}
                  onChange={() => setPlatform(p)}
                />
                {t(`platforms.${p}`)}
              </label>
            ))}
          </div>
        </div>

        <label className="form-field">
          <span className="form-field__label">{t('draft.create.captionLabel')}</span>
          <textarea
            className="form-field__textarea"
            value={captionPl}
            placeholder={t('draft.create.captionPlaceholder')}
            onChange={(e) => setCaptionPl(e.target.value)}
            rows={4}
            autoFocus
          />
        </label>

        {error && <div className="modal__error" role="alert">{error}</div>}

        <div className="modal__actions">
          <button
            type="button"
            className="button button--ghost"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button type="submit" className="button button--primary" disabled={submitting}>
            {submitting ? t('draft.create.submitting') : t('draft.create.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
